import { useEffect, useState } from 'react';
import {
  Box, Paper, Grid, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Stack, Alert, CircularProgress, LinearProgress, Chip,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import { navForRole } from '../components/officerNav';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Naira formatting for contract values.
const naira = (n) => '₦' + Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 });

function StatCard({ label, value, note }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center', flex: '1 1 150px' }}>
      <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {note ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {note}
        </Typography>
      ) : null}
    </Paper>
  );
}

// Simple horizontal bar row (no charting library): label, proportional bar, value.
function BarRow({ label, value, max, display }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">{display ?? value}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
    </Box>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{subtitle || ' '}</Typography>
      {children}
    </Paper>
  );
}

function MetricTable({ rows, valueLabel, renderValue }) {
  if (!rows.length) {
    return <Typography color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>No data yet.</Typography>;
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Reference</TableCell>
          <TableCell>Title</TableCell>
          <TableCell align="right">{valueLabel}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.slice(0, 10).map((r) => (
          <TableRow key={r.reference}>
            <TableCell>{r.reference}</TableCell>
            <TableCell sx={{ maxWidth: 220 }} title={r.title}>
              <Typography variant="body2" noWrap>{r.title}</Typography>
            </TableCell>
            <TableCell align="right">{renderValue(r)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/reports/overview');
        setData(res.data.data);
      } catch (err) {
        setError(err.response?.data?.error?.message || 'Could not load reports.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const renderBody = () => {
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return null;

    const { summary, tendersByMda, bidsPerTender, awardsBySector, timeToAward } = data;
    const maxMda = Math.max(1, ...tendersByMda.map((r) => r.count));
    const maxSector = Math.max(1, ...awardsBySector.map((r) => r.totalValue));

    return (
      <>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          <StatCard
            label="Total Tenders"
            value={summary.totalTenders}
            note={summary.historicalTenders ? `+${summary.historicalTenders} historical` : null}
          />
          <StatCard label="Published" value={summary.publishedTenders} />
          <StatCard label="Total Bids" value={summary.totalBids} />
          <StatCard
            label="Awards"
            value={summary.totalAwards}
            note={summary.historicalAwards ? `+${summary.historicalAwards} historical` : null}
          />
          <StatCard label="Vendors" value={summary.totalVendors} />
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SectionCard title="Tenders per MDA">
              {tendersByMda.length === 0
                ? <Typography color="text.secondary">No tenders yet.</Typography>
                : tendersByMda.map((r) => <BarRow key={r.mda} label={r.mda} value={r.count} max={maxMda} />)}
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Award distribution by sector" subtitle="By total contract value">
              {awardsBySector.length === 0
                ? <Typography color="text.secondary">No awards yet.</Typography>
                : awardsBySector.map((r) => (
                    <BarRow key={r.sector} label={`${r.sector} (${r.count})`} value={r.totalValue} max={maxSector} display={naira(r.totalValue)} />
                  ))}
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard
              title="Bids per tender"
              subtitle={`Average ${bidsPerTender.averageBidsPerTender} bids across ${bidsPerTender.tenderCount} tenders`}
            >
              <MetricTable rows={bidsPerTender.rows} valueLabel="Bids" renderValue={(r) => <Chip size="small" label={r.bidCount} />} />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard
              title="Time from publication to award"
              subtitle={timeToAward.awardCount >= 1
                ? `Average ${timeToAward.averageDays} days across ${timeToAward.awardCount} ${timeToAward.awardCount === 1 ? 'award' : 'awards'} run on the portal`
                : 'No awards have completed on the portal yet'}
            >
              {timeToAward.awardCount >= 1 ? (
                <>
                  <MetricTable rows={timeToAward.rows} valueLabel="Days" renderValue={(r) => r.days} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    Covers tenders both published and awarded through the portal.
                    Historical and retrospectively entered records are excluded.
                  </Typography>
                </>
              ) : (
                <Typography color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
                  A figure will appear once an award completes on the system.
                </Typography>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <DashboardLayout nav={navForRole(user?.role)} welcomeName={user?.fullName} title="Reports">
      {renderBody()}
    </DashboardLayout>
  );
}
